import { useCallback } from 'react';

import {
  InvoiceService,
  ExportService,
  InvoicePatch,
  InvoiceDelete,
  InvoiceSection,
  PaymentChannel,
  IdList,
  EntryType,
  WithoutFileCreate,
  InvoiceRejection
} from 'openapi';

import { showSuccessToastMessage } from 'components/shared/Toast/Toast';

import { useTranslations } from 'context/TranslationContext';

import { useInterceptor } from 'hooks/useInterceptor';

import { InvoiceSectionKey } from 'utils/interfaces/InvoiceProps';

export const useInvoiceController = () => {
  const { interceptRequest } = useInterceptor();

  const {
    uploadInvoice,
    getInvoicePdfById,
    deleteInvoicesByIds,
    repromptInvoiceById,
    getInvoiceById,
    getInvoiceFiltersByCompanyId,
    getInvoiceFilters,
    getInvoicesByCompanyIdAndFilters,
    getInvoicesByFilters,
    getInvoiceRowById,
    unverifyInvoiceSectionById,
    verifyInvoiceSectionById,
    updateInvoiceSectionById,
    setIsAccountedToTrue,
    markAsPaid,
    approveInvoices,
    rejectInvoices,
    createWithoutFile,
    updateInvoiceById,
    verifyWithoutFileById,
    sendInvoiceForApproval,
    returnToValidated,
    updateExpenseTypeDuringApproval,
    settleDocuments
  } = InvoiceService;

  const { exportInvoicesForPayment } = ExportService;

  const { translate } = useTranslations();

  const addInvoice = (
    id: number,
    formData: { files: Array<Blob>; entryType: EntryType }
  ) =>
    interceptRequest(
      uploadInvoice,
      {
        onSuccess: () =>
          showSuccessToastMessage(translate('messages.successfullyUploaded'))
      },
      id,
      formData
    );

  const getInvoiceFile = useCallback(
    (invoiceId: number) => interceptRequest(getInvoicePdfById, {}, invoiceId),
    [getInvoicePdfById]
  );

  const getSingleInvoiceById = useCallback(
    (companyId: number, id: number) =>
      interceptRequest(getInvoiceById, {}, companyId, id),
    [getInvoiceById]
  );

  const deleteInvoices = (requestBody: InvoiceDelete) =>
    interceptRequest(
      deleteInvoicesByIds,
      {
        onSuccess: () =>
          showSuccessToastMessage(translate('messages.successfullyDeleted'))
      },
      requestBody
    );

  const settleInvoices = (requestBody: IdList) =>
    interceptRequest(
      settleDocuments,
      {
        onSuccess: () =>
          showSuccessToastMessage(
            translate('messages.successfullySettledRevenue')
          )
      },
      requestBody
    );

  const exportSelectedInvoicesForPayment = (
    id: number,
    iban: string,
    invoiceIds: Array<number>
  ) => interceptRequest(exportInvoicesForPayment, {}, id, iban, invoiceIds);

  const unverifyInvoiceById = useCallback(
    (companyId: number, invoiceId: number, section: InvoiceSection) =>
      interceptRequest(
        unverifyInvoiceSectionById,
        {},
        companyId,
        invoiceId,
        section
      ),
    [unverifyInvoiceSectionById]
  );

  const rempromptInvoice = useCallback(
    (id: number) => interceptRequest(repromptInvoiceById, {}, id),
    [repromptInvoiceById]
  );

  const getInvoiceFiltersByCompany = useCallback(
    (companyId: number) =>
      interceptRequest(getInvoiceFiltersByCompanyId, {}, companyId),
    [getInvoiceFiltersByCompanyId]
  );

  const getInvoiceFiltersForAllCompanies = useCallback(
    () => interceptRequest(getInvoiceFilters, {}),
    [getInvoiceFilters]
  );

  const getInvoicesByCompanyAndFilters = useCallback(
    (companyId: number, filters: any) =>
      interceptRequest(
        getInvoicesByCompanyIdAndFilters,
        {},
        companyId,
        filters
      ),
    [getInvoicesByCompanyIdAndFilters]
  );

  const getFilteredInvoices = useCallback(
    (filters: any) => interceptRequest(getInvoicesByFilters, {}, filters),
    [getInvoicesByFilters]
  );

  const getInvoiceRow = useCallback(
    (id: number) => interceptRequest(getInvoiceRowById, {}, id),
    [getInvoiceRowById]
  );

  const setIsAccounted = useCallback(
    (ids: number[]) => interceptRequest(setIsAccountedToTrue, {}, { ids }),
    [setIsAccountedToTrue]
  );

  const markAsPaidByIds = useCallback(
    (paymentChannel: PaymentChannel, invoiceIds: number[]) =>
      interceptRequest(markAsPaid, {}, { paymentChannel, invoiceIds }),
    [markAsPaid]
  );

  const approveInvoicesByIds = useCallback(
    (ids: number[]) => interceptRequest(approveInvoices, {}, { ids }),
    [approveInvoices]
  );

  const rejectInvoicesByIds = useCallback(
    (ids: number[], reason?: string) =>
      interceptRequest(
        rejectInvoices,
        {},
        ids.map(
          // TODO: Reason is optional for the time-being since in InvoiceDataGrid.tsx there's no field for entering the reason for rejection yet
          // In the future, when the reason for rejection is added in the UI, we should make it mandatory here as well
          (invoiceId): InvoiceRejection => ({ invoiceId, reason: reason ?? '' })
        )
      ),
    [rejectInvoices]
  );

  const createWithoutDocument = useCallback(
    (companyId: number, requestBody: WithoutFileCreate) =>
      interceptRequest(createWithoutFile, {}, companyId, requestBody),
    [createWithoutFile]
  );

  const saveInvoiceData = useCallback(
    (companyId: number, invoiceId: number, requestBody: InvoicePatch) =>
      interceptRequest(
        updateInvoiceById,
        {},
        companyId,
        invoiceId,
        requestBody
      ),
    [updateInvoiceById]
  );

  const verifyInvoiceSection = useCallback(
    (
      companyId: number,
      invoiceId: number,
      invoiceSection: InvoiceSection,
      requestBody: InvoicePatch
    ) =>
      interceptRequest(
        verifyInvoiceSectionById,
        {},
        companyId,
        invoiceId,
        invoiceSection,
        requestBody
      ),
    []
  );

  const updateInvoice = useCallback(
    (
      companyId: number,
      invoiceId: number,
      invoiceSection: InvoiceSectionKey,
      requestBody: InvoicePatch
    ) =>
      interceptRequest(
        verifyInvoiceSectionById,
        {},
        companyId,
        invoiceId,
        invoiceSection,
        requestBody
      ),
    [verifyInvoiceSectionById, interceptRequest]
  );

  const saveInvoiceSectionData = useCallback(
    (
      companyId: number,
      invoiceId: number,
      invoiceSection: InvoiceSectionKey,
      requestBody: InvoicePatch
    ) =>
      interceptRequest(
        updateInvoiceSectionById,
        {},
        companyId,
        invoiceId,
        invoiceSection,
        requestBody
      ),
    [updateInvoiceSectionById, interceptRequest]
  );

  const saveInlineCounterparty = useCallback(
    (
      companyId: number,
      invoiceId: number,
      requestBody: InvoicePatch,
      counterpartyName: string
    ) =>
      interceptRequest(
        updateInvoiceById,
        {
          onSuccess: () =>
            showSuccessToastMessage(
              translate('messages.counterpartyCreatedAndSelected', {
                name: counterpartyName
              })
            )
        },
        companyId,
        invoiceId,
        requestBody
      ),
    [interceptRequest, translate, updateInvoiceById]
  );

  const verifyCreatedDocument = useCallback(
    (companyId: number, invoiceId: number, requestBody: InvoicePatch) =>
      interceptRequest(
        verifyWithoutFileById,
        {},
        companyId,
        invoiceId,
        requestBody
      ),
    [verifyWithoutFileById]
  );

  const sendInvoiceForApprovalById = useCallback(
    (companyId: number, invoiceId: number, requestBody: InvoicePatch) =>
      interceptRequest(
        InvoiceService.sendInvoiceForApproval,
        {
          onSuccess: () =>
            showSuccessToastMessage(
              translate('messages.successfullySentForApproval')
            )
        },
        companyId,
        invoiceId,
        requestBody
      ),
    [sendInvoiceForApproval, translate]
  );

  const returnToValidatedById = useCallback(
    (companyId: number, invoiceId: number) =>
      interceptRequest(returnToValidated, {}, companyId, invoiceId),
    [returnToValidated]
  );

  const updateExpenseTypeDuringApprovalById = useCallback(
    (companyId: number, invoiceId: number, id: number) =>
      interceptRequest(
        updateExpenseTypeDuringApproval,
        {},
        companyId,
        invoiceId,
        { id }
      ),
    [updateExpenseTypeDuringApproval]
  );

  return {
    addInvoice,
    getInvoiceFile,
    deleteInvoices,
    settleInvoices,
    exportSelectedInvoicesForPayment,
    unverifyInvoiceById,
    rempromptInvoice,
    getSingleInvoiceById,
    getInvoiceFiltersByCompany,
    getInvoiceFiltersForAllCompanies,
    getInvoicesByCompanyAndFilters,
    getFilteredInvoices,
    getInvoiceRow,
    setIsAccounted,
    markAsPaidByIds,
    approveInvoicesByIds,
    rejectInvoicesByIds,
    createWithoutDocument,
    saveInvoiceData,
    verifyInvoiceSection,
    updateInvoice,
    saveInvoiceSectionData,
    saveInlineCounterparty,
    verifyCreatedDocument,
    sendInvoiceForApprovalById,
    returnToValidatedById,
    updateExpenseTypeDuringApprovalById
  };
};
